import React, { useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import UploadColumnPicker, { UploadColumns } from './UploadColumnPicker'
import type { ThemeAIUploadInspection } from '../../types/gotypes'

export interface UploadSelection {
  file: File
  columns: UploadColumns
}

interface UploadFileStepProps {
  onInspect: (file: File) => Promise<ThemeAIUploadInspection>
  /** Called with the file and chosen columns once inspected, and on every column change; undefined when cleared. */
  onChange: (selection: UploadSelection | undefined) => void
  disabled?: boolean
}

const ACCEPTED = /\.(csv|xlsx)$/i

// Choose a file, let the server read its columns, confirm which column holds
// the responses. Nothing is imported until the caller submits the selection.
const UploadFileStep: React.FC<UploadFileStepProps> = ({ onInspect, onChange, disabled }) => {
  const [file, setFile] = useState<File>()
  const [inspection, setInspection] = useState<ThemeAIUploadInspection>()
  const [columns, setColumns] = useState<UploadColumns>({ textColumn: '', idColumn: '' })
  const [inspecting, setInspecting] = useState(false)
  const [error, setError] = useState<string>()

  const handleFile = async (chosen: File | undefined) => {
    if (!chosen) return
    if (!ACCEPTED.test(chosen.name)) {
      setError('Choose a CSV or Excel (.xlsx) file.')
      return
    }
    setError(undefined)
    setFile(chosen)
    setInspection(undefined)
    onChange(undefined)
    setInspecting(true)
    try {
      const result = await onInspect(chosen)
      const initial = { textColumn: result.suggested_text_column, idColumn: result.suggested_id_column }
      setInspection(result)
      setColumns(initial)
      onChange({ file: chosen, columns: initial })
    } catch (err) {
      setFile(undefined)
      setError(err instanceof Error ? err.message : 'This file could not be read.')
    } finally {
      setInspecting(false)
    }
  }

  const handleColumns = (next: UploadColumns) => {
    setColumns(next)
    if (file) onChange({ file, columns: next })
  }

  const handleClear = () => {
    setFile(undefined)
    setInspection(undefined)
    onChange(undefined)
  }

  if (file && inspection) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <UploadColumnPicker fileName={file.name} inspection={inspection} value={columns} onChange={handleColumns} />
        <Box>
          <Button size="small" color="info" onClick={handleClear} disabled={disabled} sx={{ ml: -1 }}>
            Choose a different file
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        if (!disabled && !inspecting) handleFile(event.dataTransfer.files?.[0])
      }}
      sx={{
        border: '1px dashed',
        borderColor: error ? 'error.main' : 'divider',
        borderRadius: 1,
        p: 4,
        textAlign: 'center',
      }}
    >
      <input
        type="file"
        accept=".csv,.xlsx"
        id="theme-ai-upload-file"
        hidden
        disabled={disabled || inspecting}
        onChange={(event) => {
          handleFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />
      <label htmlFor="theme-ai-upload-file" style={{ cursor: inspecting ? 'progress' : 'pointer', display: 'block' }}>
        {inspecting ? (
          <>
            <CircularProgress size={28} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">Reading {file?.name}…</Typography>
          </>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2">Drop a CSV or Excel file here, or click to browse</Typography>
            <Typography variant="caption" color="text.secondary">
              You will choose which column holds the responses next.
            </Typography>
          </>
        )}
      </label>
      {error && <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{error}</Alert>}
    </Box>
  )
}

export default UploadFileStep
